import React from 'react';
import { motion } from 'framer-motion';
import { PageHero } from '../components/PageHero';
import { SectionLabel, SectionHeading, SecondaryButton } from '../components/ui/UI';
import { JoinTeamForm } from '../components/JoinTeamForm';
import { SectionStage } from '../components/SectionStage';
const roles = ['Founding Full-Stack Engineer', 'Founding Security Engineer', 'AI Systems Engineer', 'Security Researcher / Validation Engineer', 'Product Designer', 'MSSP / Partner GTM Lead', 'Developer & Security Community Builder'];
const culture = ['Move fast. Verify things.', 'Make hard security concepts usable.', 'Do not hide behind jargon.', 'Safety is part of the product.', 'Evidence wins.'];
const cares = ['Security validation', 'Control testing', 'Attack-path reasoning', 'Cloud and identity security', 'AI application security', 'Evidence graphs', 'Remediation verification', 'MSSP workflows', 'Plainspoken product design', 'Making enterprise security less ridiculous'];
export function JobsPage() {
  return <>
      <PageHero eyebrow="Jobs" title="Help rebuild" titleMuted="the company that saw it early." description="We're building the self-service validation layer for modern security. We care about proof, not dashboards. Plain English, not acronym soup." bg="none" data-id="element-1622" />

      <SectionStage fx="particles" data-id="element-1623">
        <section className="relative w-full py-32 border-t border-white/5" data-id="element-1624">
          <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-1625">
            <SectionLabel number="01" label="What we care about" className="mb-8" data-id="element-1626" />

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16" data-id="element-1627">
              <div className="lg:col-span-8" data-id="element-1628">
                <SectionHeading size="xl" data-id="element-1629">
                  Validation work
                  <br data-id="element-1630" />
                  <span className="text-white/40" data-id="element-1631">that actually matters.</span>
                </SectionHeading>
              </div>
              <div className="lg:col-span-4 lg:pt-4" data-id="element-1632">
                <p className="text-base text-white/60 leading-relaxed" data-id="element-1633">
                  We are interested in people who want to make enterprise
                  security less ridiculous — and have receipts to show for it.
                </p>
              </div>
            </div>

            <ul className="flex flex-wrap gap-x-8 gap-y-3 mb-20" data-id="element-1634">
              {cares.map((c, i) => <motion.li key={c} initial={{
              opacity: 0
            }} whileInView={{
              opacity: 1
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.3,
              delay: i * 0.02
            }} className="text-base text-white/80 flex items-center gap-2" data-id="element-1635">
                  <span className="font-mono text-xs text-[#1a8fd4]/60" data-id="element-1636">·</span>
                  {c}
                </motion.li>)}
            </ul>
          </div>
        </section>
      </SectionStage>

      <SectionStage fx="beams" data-id="element-1637">
        <section className="relative w-full py-32 border-t border-white/5" data-id="element-1638">
          <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-1639">
            <SectionLabel number="02" label="Early Roles" className="mb-8" data-id="element-1640" />

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-12" data-id="element-1641">
              <div className="lg:col-span-8" data-id="element-1642">
                <SectionHeading size="xl" data-id="element-1643">
                  Founding seats
                  <br data-id="element-1644" />
                  <span className="text-white/40" data-id="element-1645">are open.</span>
                </SectionHeading>
              </div>
            </div>

            <ol className="border-t border-white/5 mb-12" data-id="element-1646">
              {roles.map((r, i) => <motion.li key={r} initial={{
              opacity: 0,
              x: 6
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.35,
              delay: i * 0.04
            }} className="border-b border-white/5 py-5 flex items-baseline gap-6" data-id="element-1647">
                  <span className="font-mono text-sm text-[#1a8fd4]/70 tracking-[0.2em] w-10" data-id="element-1648">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <span className="text-lg text-white font-medium" data-id="element-1649">{r}</span>
                </motion.li>)}
            </ol>

            <p className="text-base text-white/50 max-w-2xl mb-8" data-id="element-1650">
              Don't see the perfect role? Good. We're early. Tell us how you
              want to help.
            </p>

            <div className="max-w-3xl" data-id="element-1651">
              <JoinTeamForm data-id="element-1652" />
              <div className="mt-6 flex justify-center" data-id="element-1653">
                <SecondaryButton href="/early-access" size="md" data-id="element-1654">
                  Or join Early Access first
                </SecondaryButton>
              </div>
            </div>
          </div>
        </section>
      </SectionStage>

      <SectionStage fx="glow-orbs" data-id="element-1655">
        <section className="relative w-full py-32 border-t border-white/5" data-id="element-1656">
          <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-1657">
            <SectionLabel number="03" label="Culture" className="mb-8" data-id="element-1658" />

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12" data-id="element-1659">
              <div className="lg:col-span-8" data-id="element-1660">
                <SectionHeading size="xl" data-id="element-1661">
                  Five rules
                  <br data-id="element-1662" />
                  <span className="text-white/40" data-id="element-1663">we live by.</span>
                </SectionHeading>
              </div>
            </div>

            <ol className="mt-12 border-t border-white/5" data-id="element-1664">
              {culture.map((c, i) => <motion.li key={c} initial={{
              opacity: 0,
              y: 12
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.4,
              delay: i * 0.05
            }} className="border-b border-white/5 py-7 flex items-baseline gap-8" data-id="element-1665">
                  <span className="font-mono text-sm text-[#1a8fd4]/70 tracking-[0.2em] w-10" data-id="element-1666">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <p className="text-2xl md:text-3xl text-white font-medium tracking-tight leading-snug" data-id="element-1667">
                    {c}
                  </p>
                </motion.li>)}
            </ol>
          </div>
        </section>
      </SectionStage>
    </>;
}